/**
 * RemindersPage — Family Reminders & Tribute Cards dashboard.
 *
 * Sections:
 *   • Upcoming (next 30 days)
 *   • All reminders
 *   • Suggested (from people with birth/death dates — not yet enabled)
 *
 * Actions per reminder:
 *   • Enable / Disable toggle
 *   • Edit
 *   • Create tribute card
 *   • Delete
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Bell, BellOff, Plus, Calendar, Loader2, Pencil, Trash2,
  Sparkles, ChevronRight, Clock,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { AddReminderDialog } from '../components/reminders/AddReminderDialog';
import { DigitalCardPreview } from '../components/reminders/DigitalCardPreview';
import { MinuteOfSilence } from '../components/reminders/MinuteOfSilence';
import { ArchivePageShell } from '../components/archive/ArchivePageShell';
import {
  remindersApi,
  normalizeReminderList,
  REMINDER_TYPE_LABELS, REMINDER_TYPE_ICONS, REMINDER_TIMING_LABELS,
  REMINDER_REPEAT_LABELS, formatReminderDate, daysUntilCopy, daysUntilColor,
} from '../utils/reminders';

export default function RemindersPage() {
  const [upcoming, setUpcoming] = useState([]);
  const [all, setAll] = useState([]);
  const [loadingUpcoming, setLoadingUpcoming] = useState(true);
  const [loadingAll, setLoadingAll] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editReminder, setEditReminder] = useState(null);
  const [cardReminder, setCardReminder] = useState(null);
  const [silenceReminder, setSilenceReminder] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadUpcoming = useCallback(async () => {
    setLoadingUpcoming(true);
    try {
      setUpcoming(normalizeReminderList(await remindersApi.upcoming(30)));
    } catch {
      setUpcoming([]);
    } finally {
      setLoadingUpcoming(false);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoadingAll(true);
    try {
      setAll(normalizeReminderList(await remindersApi.list()));
    } catch {
      setAll([]);
    } finally {
      setLoadingAll(false);
    }
  }, []);

  useEffect(() => { loadUpcoming(); loadAll(); }, [loadUpcoming, loadAll]);

  const handleSaved = () => { loadUpcoming(); loadAll(); };

  const handleToggle = async (r) => {
    try {
      const updated = await remindersApi.update(r.id, { enabled: !r.enabled });
      setAll((arr) => normalizeReminderList(arr).map((x) => (x.id === updated.id ? updated : x)));
      setUpcoming((arr) => normalizeReminderList(arr).filter((x) => x.id !== updated.id || updated.enabled));
    } catch { toast.error('Could not update reminder.'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await remindersApi.delete(deleteTarget.id);
      setAll((arr) => normalizeReminderList(arr).filter((x) => x.id !== deleteTarget.id));
      setUpcoming((arr) => normalizeReminderList(arr).filter((x) => x.id !== deleteTarget.id));
      setDeleteTarget(null);
      toast.success('Reminder deleted.');
    } catch { toast.error('Could not delete reminder.'); }
    finally { setDeleting(false); }
  };

  const allReminders = normalizeReminderList(all);
  const upcomingReminders = normalizeReminderList(upcoming);

  return (
    <ArchivePageShell>
    <div className="min-h-screen bg-[hsl(42_28%_97%)]">
      {/* Page header */}
      <div className="border-b border-[hsl(38_14%_88%)] bg-[hsl(42_28%_99%)]">
        <div className="mx-auto max-w-3xl px-6 py-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/60">
                Family Reminders &amp; Tribute Cards
              </p>
              <h1 className="font-serif text-3xl font-light text-[#1c1917]">Reminders</h1>
              <p className="mt-1.5 text-sm text-muted-foreground/80">
                Birthdays, remembrance days, anniversaries — keep the moments that matter close. One day, their voice becomes priceless.
              </p>
            </div>
            <Button
              type="button"
              className="shrink-0 gap-2 rounded-full"
              onClick={() => { setEditReminder(null); setAddOpen(true); }}
            >
              <Plus className="h-4 w-4" />
              Add reminder
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-6 py-8 space-y-8">
        {/* Upcoming — always visible */}
        <section>
          <div className="mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground/50" />
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/60">
              Coming up in the next 30 days
            </h2>
          </div>

          {loadingUpcoming && <LoadingRow />}

          {!loadingUpcoming && upcomingReminders.length === 0 && (
            <EmptyState
              icon={<Calendar className="h-8 w-8 text-muted-foreground/40" />}
              title="Keep important family dates close."
              description="Birthdays, remembrance days and anniversaries — add one to see it here in the next 30 days."
              action={
                <Button type="button" variant="outline" className="mt-4 rounded-full" onClick={() => { setEditReminder(null); setAddOpen(true); }}>
                  <Plus className="mr-1.5 h-4 w-4" />
                  Add reminder
                </Button>
              }
            />
          )}

          {!loadingUpcoming && upcomingReminders.length > 0 && (
            <div className="space-y-3">
              {upcomingReminders.map((r) => (
                <UpcomingReminderCard
                  key={r.id}
                  reminder={r}
                  onEdit={() => { setEditReminder(r); setAddOpen(true); }}
                  onCard={() => setCardReminder(r)}
                  onSilence={() => setSilenceReminder(r)}
                />
              ))}
            </div>
          )}
        </section>

        {/* Tabbed management view */}
        <Tabs defaultValue="all">
          <TabsList className="rounded-full border border-[hsl(38_14%_88%)] bg-muted/40 p-1 h-auto gap-1">
            <TabsTrigger value="all" className="rounded-full px-4 py-1.5 text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm">
              All reminders
            </TabsTrigger>
            <TabsTrigger value="cards" className="rounded-full px-4 py-1.5 text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Tribute cards
            </TabsTrigger>
          </TabsList>

          {/* All reminders */}
          <TabsContent value="all" className="mt-4 space-y-2">
            {loadingAll && <LoadingRow />}
            {!loadingAll && allReminders.length === 0 && (
              <EmptyState
                icon={<Bell className="h-8 w-8 text-muted-foreground/40" />}
                title="Keep important family dates close."
                description="Birthdays, remembrance days, anniversaries — private reminders for the moments that matter."
                action={
                  <Button type="button" className="mt-4 rounded-full" onClick={() => { setEditReminder(null); setAddOpen(true); }}>
                    <Plus className="mr-1.5 h-4 w-4" />
                    Add reminder
                  </Button>
                }
              />
            )}
            {!loadingAll && allReminders.map((r) => (
              <ReminderListItem
                key={r.id}
                reminder={r}
                onToggle={() => handleToggle(r)}
                onEdit={() => { setEditReminder(r); setAddOpen(true); }}
                onCard={() => setCardReminder(r)}
                onDelete={() => setDeleteTarget(r)}
              />
            ))}
          </TabsContent>

          {/* Tribute cards */}
          <TabsContent value="cards" className="mt-4">
            {allReminders.length === 0 ? (
              <EmptyState
                icon={<Sparkles className="h-8 w-8 text-muted-foreground/30" />}
                title="Tribute cards appear here"
                description="Once you have reminders set up, create a tribute card for birthdays, remembrance days, and anniversaries."
              />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {allReminders.map((r) => (
                  <CardThumbnail key={r.id} reminder={r} onClick={() => setCardReminder(r)} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* ---- Dialogs ---- */}

      {/* Add / edit reminder */}
      <AddReminderDialog
        open={addOpen}
        onOpenChange={(v) => { setAddOpen(v); if (!v) setEditReminder(null); }}
        editReminder={editReminder}
        onSaved={handleSaved}
      />

      {/* Tribute card preview */}
      <Dialog open={Boolean(cardReminder)} onOpenChange={(v) => !v && setCardReminder(null)}>
        <DialogContent className="sm:max-w-sm border-[#e8e2d8] bg-[#fffcf7] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl font-normal">Tribute card</DialogTitle>
          </DialogHeader>
          {cardReminder && (
            <>
              <DigitalCardPreview reminder={cardReminder} />
              {/* Minute of silence for remembrance types */}
              {['death_anniversary', 'remembrance_day'].includes(cardReminder.reminder_type) && (
                <div className="mt-2">
                  <MinuteOfSilence
                    personName={cardReminder.person_name}
                    onComplete={(action) => {
                      setCardReminder(null);
                      if (action === 'memory' && cardReminder.person_id) {
                        window.location.href = `/person/${cardReminder.person_id}?view=stories`;
                      }
                    }}
                  />
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Minute of silence (standalone) */}
      <Dialog open={Boolean(silenceReminder)} onOpenChange={(v) => !v && setSilenceReminder(null)}>
        <DialogContent className="sm:max-w-sm border-[#e8e2d8] bg-[#0f0f1a] max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl font-normal text-white">A quiet moment</DialogTitle>
          </DialogHeader>
          {silenceReminder && (
            <MinuteOfSilence
              personName={silenceReminder.person_name}
              onComplete={(action) => {
                setSilenceReminder(null);
                if (action === 'memory' && silenceReminder.person_id) {
                  window.location.href = `/person/${silenceReminder.person_id}?view=stories`;
                }
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={Boolean(deleteTarget)} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-sm border-[#e8e2d8] bg-[#fffcf7]">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg font-normal">Delete reminder?</DialogTitle>
            <DialogDescription>
              "{deleteTarget?.title}" will be permanently removed.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 pt-2">
            <Button variant="destructive" className="rounded-full" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
            <Button variant="ghost" className="rounded-full" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </ArchivePageShell>
  );
}


// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function UpcomingReminderCard({ reminder, onEdit, onCard, onSilence }) {
  const icon = REMINDER_TYPE_ICONS[reminder.reminder_type] || '📅';
  const typeLabel = REMINDER_TYPE_LABELS[reminder.reminder_type] || 'Reminder';
  const dateStr = formatReminderDate(reminder.date_month, reminder.date_day, reminder.date_year);
  const daysCopy = daysUntilCopy(reminder.days_until);
  const daysColor = daysUntilColor(reminder.days_until);
  const isRemembrance = ['death_anniversary', 'remembrance_day'].includes(reminder.reminder_type);

  return (
    <Card className="border-[hsl(38_14%_88%)] bg-white shadow-none hover:border-[hsl(38_14%_78%)] transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <span className="mt-0.5 text-2xl leading-none shrink-0">{icon}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <p className="font-medium text-foreground/90 leading-snug truncate">{reminder.title}</p>
              <span className={`shrink-0 text-sm font-medium tabular-nums ${daysColor}`}>{daysCopy}</span>
            </div>
            {reminder.person_name && (
              <p className="mt-0.5 text-sm text-muted-foreground/70">{reminder.person_name}</p>
            )}
            <p className="mt-0.5 text-xs text-muted-foreground/50">
              {typeLabel} · {dateStr} · {REMINDER_TIMING_LABELS[reminder.reminder_timing]}
            </p>

            <div className="mt-3 flex flex-wrap gap-2">
              {reminder.person_id && (
                <Link to={`/person/${reminder.person_id}`}>
                  <Button size="sm" variant="outline" className="h-7 rounded-full px-3 text-xs gap-1.5">
                    <ChevronRight className="h-3 w-3" /> View archive
                  </Button>
                </Link>
              )}
              <Button size="sm" variant="outline" className="h-7 rounded-full px-3 text-xs" onClick={onCard}>
                Create card
              </Button>
              {isRemembrance && (
                <Button size="sm" variant="ghost" className="h-7 rounded-full px-3 text-xs text-muted-foreground" onClick={onSilence}>
                  🕯️ Quiet moment
                </Button>
              )}
              <Button size="sm" variant="ghost" className="h-7 rounded-full px-3 text-xs text-muted-foreground" onClick={onEdit}>
                Edit
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


function ReminderListItem({ reminder, onToggle, onEdit, onCard, onDelete }) {
  const icon = REMINDER_TYPE_ICONS[reminder.reminder_type] || '📅';
  const typeLabel = REMINDER_TYPE_LABELS[reminder.reminder_type] || 'Reminder';
  const dateStr = formatReminderDate(reminder.date_month, reminder.date_day, reminder.date_year);

  return (
    <div className="flex items-center gap-3 rounded-xl border border-[hsl(38_14%_88%)] bg-white px-4 py-3 hover:border-[hsl(38_14%_78%)] transition-colors">
      <span className="shrink-0 text-lg leading-none">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-medium leading-snug truncate ${!reminder.enabled ? 'opacity-50' : ''}`}>{reminder.title}</p>
        <p className="text-[11px] text-muted-foreground/60">
          {typeLabel} · {dateStr}
          {reminder.person_name ? ` · ${reminder.person_name}` : ''}
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button size="sm" variant="ghost" className="h-7 w-7 rounded-full p-0" onClick={onCard} title="Tribute card">
          ✨
        </Button>
        <Button size="sm" variant="ghost" className="h-7 w-7 rounded-full p-0" onClick={onEdit} title="Edit">
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <button
          type="button"
          onClick={onToggle}
          className={`h-7 w-7 rounded-full transition-colors flex items-center justify-center ${
            reminder.enabled ? 'text-primary/70 hover:text-muted-foreground' : 'text-muted-foreground/40 hover:text-primary'
          }`}
          title={reminder.enabled ? 'Disable' : 'Enable'}
        >
          {reminder.enabled ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
        </button>
        <Button size="sm" variant="ghost" className="h-7 w-7 rounded-full p-0 text-muted-foreground/40 hover:text-red-500" onClick={onDelete} title="Delete">
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}


function CardThumbnail({ reminder, onClick }) {
  const icon = REMINDER_TYPE_ICONS[reminder.reminder_type] || '📅';
  const CARD_THEMES = {
    birthday: '#2C4A3B',
    death_anniversary: '#1a1a2e',
    remembrance_day: '#1a1a2e',
    wedding_anniversary: '#3b2f2f',
    custom: '#2C4A3B',
  };
  const bg = CARD_THEMES[reminder.reminder_type] || '#2C4A3B';

  return (
    <button
      type="button"
      onClick={onClick}
      className="group w-full overflow-hidden rounded-xl border border-[hsl(38_14%_88%)] text-left transition hover:scale-[1.02] hover:shadow-md"
    >
      <div
        className="flex flex-col items-center justify-center px-4 py-8 text-white"
        style={{ background: bg, minHeight: '120px' }}
      >
        <span className="mb-1 text-2xl">{icon}</span>
        <p className="font-serif text-sm font-light opacity-90">{reminder.person_name || reminder.title}</p>
        <p className="mt-1 text-[10px] font-semibold uppercase tracking-widest opacity-50">
          {REMINDER_TYPE_LABELS[reminder.reminder_type] || 'Card'}
        </p>
      </div>
      <div className="bg-white px-3 py-2">
        <p className="text-xs font-medium truncate text-foreground/70">{reminder.title}</p>
        <p className="text-[11px] text-muted-foreground/50">
          {formatReminderDate(reminder.date_month, reminder.date_day, reminder.date_year)}
        </p>
      </div>
    </button>
  );
}


function EmptyState({ icon, title, description, action }) {
  return (
    <div className="rounded-2xl border border-dashed border-[hsl(38_14%_86%)] bg-gradient-to-b from-[hsl(42_28%_99%)] to-[hsl(38_22%_96%)] px-8 py-10 text-center">
      <div className="mb-3 flex justify-center">{icon}</div>
      <p className="font-serif text-lg text-foreground/80">{title}</p>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground/80">{description}</p>
      {action}
    </div>
  );
}


function LoadingRow() {
  return (
    <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground/60">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading…
    </div>
  );
}
